import { proxyCustomElement, HTMLElement, h, Host } from '@stencil/core/internal/client';

const delay = (time) => new Promise((resolve) => setTimeout(resolve, time));

const rocheAnimatedWordsCss = "@keyframes text-animation-first{0%{transform:translateY(0%)}75%{transform:translateY(0%)}100%{transform:translateY(-100%)}}@keyframes text-animation-full{0%{transform:translateY(100%)}25%{transform:translateY(0%)}75%{transform:translateY(0%)}100%{transform:translateY(-100%)}}@keyframes text-animation-last{0%{transform:translateY(100%)}25%{transform:translateY(0%)}100%{transform:translateY(0%)}}:host{display:block;margin-top:3rem;display:block;margin-top:3rem}@media (min-width: 90rem){:host{margin-top:4.5rem}}:host,:host *{box-sizing:border-box}@media (min-width: 48rem){:host{margin-top:4rem}}@media (min-width: 90rem){:host{margin-top:6rem}}:host .words-container{position:relative;overflow:hidden;opacity:1;transition:opacity 75ms ease-in}:host .words-container--is-restarting{opacity:0}:host .words-container--animate{height:3rem}@media (min-width: 48rem){:host .words-container--animate{height:4.5625rem}}@media (min-width: 90rem){:host .words-container--animate{height:6.3125rem}}:host .headline{padding:0;color:var(--color-text--primary);font-family:\"roche-sans\";font-style:normal;font-weight:200;font-size:2.5rem;line-height:3rem}:host .headline strong{font-weight:600}@media (min-width: 48rem){:host .headline{font-size:3rem;line-height:3.375rem}}@media (min-width: 90rem){:host .headline{font-size:3.8125rem;line-height:4.375rem}}@media (min-width: 48rem){:host .headline{padding:0;color:var(--color-text--primary);font-family:\"roche-sans\";font-style:normal;font-weight:200;font-size:3.5rem;line-height:4.1875rem}:host .headline strong{font-weight:600}}@media (min-width: 48rem) and (min-width: 48rem){:host .headline{font-size:4rem;line-height:4.5625rem}}@media (min-width: 48rem) and (min-width: 90rem){:host .headline{font-size:5.5rem;line-height:6.3125rem}}:host .word-wrapper{width:fit-content;position:relative;top:0;animation-duration:1s;animation-timing-function:cubic-bezier(0, 0, 0, 1);animation-fill-mode:forwards;animation-iteration-count:1}:host .word-wrapper--animate{position:absolute}:host .word{padding:0;color:var(--color-text--primary);font-family:\"roche-sans\";font-style:normal;font-weight:200;font-size:2.5rem;line-height:3rem;font-weight:400}:host .word strong{font-weight:600}@media (min-width: 48rem){:host .word{font-size:3rem;line-height:3.375rem}}@media (min-width: 90rem){:host .word{font-size:3.8125rem;line-height:4.375rem}}@media (min-width: 48rem){:host .word{padding:0;color:var(--color-text--primary);font-family:\"roche-sans\";font-style:normal;font-weight:200;font-size:3.5rem;line-height:4.1875rem;font-weight:400}:host .word strong{font-weight:600}}@media (min-width: 48rem) and (min-width: 48rem){:host .word{font-size:4rem;line-height:4.5625rem}}@media (min-width: 48rem) and (min-width: 90rem){:host .word{font-size:5.5rem;line-height:6.3125rem}}:host .word-red{color:#8c0000}:host .word-orange{color:#b22b0d}:host .word-purple{color:#7d0096}:host .word-brand{color:#1482fa}:host .word-effect{background:#0b41cd;-webkit-background-clip:text;background-clip:text;-webkit-text-fill-color:transparent}";
const RocheAnimatedWordsStyle0 = rocheAnimatedWordsCss;

const RocheAnimatedWords$1 = /*@__PURE__*/ proxyCustomElement(class RocheAnimatedWords extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.colors = ['red', 'orange', 'purple', 'brand'];
        this.animationDelay = 750;
        this.entersViewport = (data) => {
            var _a;
            if (data[0].isIntersecting) {
                this.wordsList = this.parsedWords;
                (_a = this.observer) === null || _a === void 0 ? void 0 : _a.disconnect();
            }
        };
        this.headline = undefined;
        this.words = undefined;
        this.wordsList = [];
        this.isRestarting = false;
    }
    get parsedWords() {
        if (!this.words) {
            return [];
        }
        return this.words.trim().split('\n');
    }
    componentDidLoad() {
        this.wordsContainer = this.element.shadowRoot.querySelector('.words-container');
        if (this.wordsContainer) {
            this.observer = new IntersectionObserver(this.entersViewport.bind(this), { threshold: [0.5] });
            this.observer.observe(this.wordsContainer);
        }
        if (!this.shouldAnimate) {
            return;
        }
        this.wordsContainer.addEventListener('animationstart', this.handleAnimationRestart.bind(this));
    }
    render() {
        return (h(Host, { key: 'f5728286ac57b218b92d260cbdca1cb6f6a6337a' }, h("div", { key: 'cb6f97b4061d68c0f1bd0b9a3996a1f8681662cf', class: "headline" }, this.headline), h("div", { key: 'c7c6a059cb5719ff77dfb8f85d6f497aa343b153', class: {
                'words-container': true,
                'words-container--is-restarting': this.isRestarting,
                'words-container--animate': this.shouldAnimate,
            } }, this.wordsList.map((word, index) => h("div", { key: index, class: {
                'word-wrapper': true,
                'word-wrapper--animate': this.shouldAnimate,
            }, style: this.getStyle(index, this.wordsList.length) }, h("span", { class: `word word-${this.getColor(index, this.wordsList.length)}` }, word))))));
    }
    getColor(index, maxIndex) {
        if (index === maxIndex - 1) {
            return 'effect';
        }
        return this.colors[index % this.colors.length];
    }
    getStyle(index, maxIndex) {
        if (!this.shouldAnimate) {
            return {
                transform: 'translateY(0%)',
            };
        }
        let animationName = 'text-animation-full';
        if (index === 0) {
            animationName = 'text-animation-first';
        }
        if (index === maxIndex - 1) {
            animationName = 'text-animation-last';
        }
        return {
            animationDelay: `${this.animationDelay * index}ms`,
            animationName,
            transform: index === 0 ? 'translateY(0%)' : 'translateY(100%)',
        };
    }
    async handleAnimationRestart(event) {
        const { animationName } = event;
        if (animationName !== 'text-animation-last') {
            return;
        }
        await delay(6000);
        this.isRestarting = true;
        await delay(250);
        this.wordsList = [];
        await delay(50);
        this.wordsList = this.parsedWords;
        this.isRestarting = false;
    }
    get shouldAnimate() {
        return this.parsedWords.length > 1;
    }
    get element() { return this; }
    static get style() { return RocheAnimatedWordsStyle0; }
}, [1, "roche-animated-words", {
        "headline": [1],
        "words": [1],
        "wordsList": [32],
        "isRestarting": [32]
    }]);
function defineCustomElement$1() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["roche-animated-words"];
    components.forEach(tagName => { switch (tagName) {
        case "roche-animated-words":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, RocheAnimatedWords$1);
            }
            break;
    } });
}

const RocheAnimatedWords = RocheAnimatedWords$1;
const defineCustomElement = defineCustomElement$1;

export { RocheAnimatedWords, defineCustomElement };

//# sourceMappingURL=roche-animated-words.js.map